/**
 * AccessChangeRenderer — Sprint 12 hand-designed renderer for displayType='access_change'.
 *
 * Per spec §8.5: "Permission-diff visualizer; before/after groups; risk badge".
 *
 * Layout:
 *   - Header: target user + risk badge (color-keyed: low/medium/high/critical).
 *   - Diff section: two columns. Left = "Current", right = "Proposed".
 *     Inside each: groups list + permissions list. Items present only on the
 *     RIGHT (additions) are highlighted green; items present only on the LEFT
 *     (removals) are highlighted red. Unchanged items stay neutral.
 *   - Justification field (editable multiline, often aiEditable) — rendered
 *     via the shared FieldRow primitive so edit-chain integrity holds.
 *
 * Evidence shape (we tolerate both flat and namespaced layouts):
 *   evidence.target_user (string | { id, name, email })
 *   evidence.risk_level  ('low' | 'medium' | 'high' | 'critical' | string)
 *   evidence.before.groups       string[]
 *   evidence.before.permissions  string[]
 *   evidence.after.groups        string[]
 *   evidence.after.permissions   string[]
 *   evidence.justification       string  (also typically an editable field)
 */

import * as React from 'react';
import { ShieldAlert, UserCog, Minus, Plus, Equal } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { readPath } from '@argusai/shared';
import type { FieldDef } from '@argusai/shared';
import { FieldRow } from '../AutoRenderedApprovalCard';
import { ActionBar } from '../ActionBar';
import { cn } from '@/lib/utils';
import type { RendererComponent } from '../AutoRenderedApprovalCard';

type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

const RISK_VARIANTS: Record<
  RiskLevel,
  { label: string; cls: string }
> = {
  low: {
    label: 'Low risk',
    cls: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/30 dark:text-emerald-300',
  },
  medium: {
    label: 'Medium risk',
    cls: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/30 dark:text-amber-300',
  },
  high: {
    label: 'High risk',
    cls: 'bg-orange-100 text-orange-900 border-orange-300 dark:bg-orange-950/30 dark:text-orange-300',
  },
  critical: {
    label: 'Critical risk',
    cls: 'bg-red-100 text-red-900 border-red-300 dark:bg-red-950/30 dark:text-red-300',
  },
};

function asString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

function asStringArray(v: unknown): string[] {
  if (Array.isArray(v)) {
    return v.map((x) => asString(x)).filter((s) => s !== '');
  }
  return [];
}

function normalizeRisk(raw: unknown): RiskLevel {
  const s = asString(raw).toLowerCase();
  if (s === 'low' || s === 'medium' || s === 'high' || s === 'critical') {
    return s;
  }
  // Tolerate common synonyms — workflow authors ship a lot of variants.
  if (['safe', 'minor'].includes(s)) return 'low';
  if (['moderate'].includes(s)) return 'medium';
  if (['severe', 'sensitive'].includes(s)) return 'high';
  if (['emergency', 'p0'].includes(s)) return 'critical';
  return 'medium';
}

function findField(
  fields: FieldDef[],
  paths: readonly string[],
): FieldDef | undefined {
  for (const p of paths) {
    const f = fields.find((x) => x.path === p);
    if (f) return f;
  }
  return undefined;
}

function describeTargetUser(raw: unknown): string {
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object') {
    const u = raw as Record<string, unknown>;
    const name = asString(u.name ?? u.displayName);
    const email = asString(u.email);
    const id = asString(u.id ?? u.userId);
    return [name || id, email].filter(Boolean).join(' · ') || '—';
  }
  return '—';
}

function diff(left: string[], right: string[]) {
  const ls = new Set(left);
  const rs = new Set(right);
  const removed = left.filter((x) => !rs.has(x));
  const added = right.filter((x) => !ls.has(x));
  const unchanged = left.filter((x) => rs.has(x));
  return { removed, added, unchanged };
}

function DiffList({
  title,
  removed,
  unchanged,
  side,
}: {
  title: string;
  removed: string[];
  unchanged: string[];
  side: 'left' | 'right';
}) {
  // For the LEFT side we render unchanged + removed (struck through).
  // For the RIGHT side we render unchanged + the "added" items in green —
  // but `added` is computed from the right's perspective and passed in via
  // the parent's `removed` array swap.
  return (
    <ul className="space-y-1" data-diff-side={side} data-diff-title={title}>
      {unchanged.length === 0 && removed.length === 0 && (
        <li className="text-xs italic text-muted-foreground">— none —</li>
      )}
      {unchanged.map((item) => (
        <li
          key={`u-${item}`}
          className="flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs text-muted-foreground"
          data-diff-status="unchanged"
        >
          <Equal className="h-3 w-3 flex-shrink-0 opacity-40" />
          <span className="truncate" title={item}>
            {item}
          </span>
        </li>
      ))}
      {removed.map((item) => (
        <li
          key={`r-${item}`}
          className={cn(
            'flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs',
            side === 'left'
              ? 'bg-red-50 text-red-900 line-through dark:bg-red-950/30 dark:text-red-300'
              : 'bg-emerald-50 font-medium text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300',
          )}
          data-diff-status={side === 'left' ? 'removed' : 'added'}
        >
          {side === 'left' ? (
            <Minus className="h-3 w-3 flex-shrink-0" />
          ) : (
            <Plus className="h-3 w-3 flex-shrink-0" />
          )}
          <span className="truncate" title={item}>
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

export const AccessChangeRenderer: RendererComponent = ({
  approval,
  schema,
  evidence,
  onFieldChange,
  onDecide,
  disabled,
}) => {
  // Target user — accept string or rich record.
  const targetUserRaw =
    readPath(evidence, 'target_user') ?? readPath(evidence, 'content.target_user');
  const targetUserLabel = describeTargetUser(targetUserRaw);

  // Risk.
  const risk = normalizeRisk(
    readPath(evidence, 'risk_level') ?? readPath(evidence, 'content.risk_level'),
  );
  const riskVariant = RISK_VARIANTS[risk];

  // Permission/group lists — accept both `before/after` and
  // `content.before/content.after` namespacing.
  const beforeGroups = asStringArray(
    readPath(evidence, 'before.groups') ??
      readPath(evidence, 'content.before.groups'),
  );
  const beforePerms = asStringArray(
    readPath(evidence, 'before.permissions') ??
      readPath(evidence, 'content.before.permissions'),
  );
  const afterGroups = asStringArray(
    readPath(evidence, 'after.groups') ??
      readPath(evidence, 'content.after.groups'),
  );
  const afterPerms = asStringArray(
    readPath(evidence, 'after.permissions') ??
      readPath(evidence, 'content.after.permissions'),
  );

  const groupsDiff = React.useMemo(
    () => diff(beforeGroups, afterGroups),
    [beforeGroups, afterGroups],
  );
  const permsDiff = React.useMemo(
    () => diff(beforePerms, afterPerms),
    [beforePerms, afterPerms],
  );

  // Justification field — workflow authors typically declare this as
  // editableField + aiEditable. We render via the standard FieldRow when
  // declared; otherwise we fall back to a static viewer.
  const justificationField = findField(schema.editableFields, [
    'justification',
    'content.justification',
  ]);
  const justificationValue = asString(
    readPath(
      evidence,
      justificationField?.path ?? 'justification',
    ) ?? readPath(evidence, 'content.justification'),
  );

  // Any other editable fields the author declared — render them at the
  // bottom of the left column so authors retain a surface for, e.g.,
  // ticket-link or change-request-id fields.
  const extras = schema.editableFields.filter(
    (f) => f.path !== justificationField?.path,
  );

  const summary = {
    groupsAdded: groupsDiff.added.length,
    groupsRemoved: groupsDiff.removed.length,
    permsAdded: permsDiff.added.length,
    permsRemoved: permsDiff.removed.length,
  };

  return (
    <div className="grid gap-6 p-4 lg:grid-cols-[2fr,1fr]">
      <div className="space-y-4" data-renderer="access_change">
        {/* Header */}
        <header className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/30 p-3">
          <div className="flex items-center gap-2">
            <UserCog className="h-4 w-4 text-primary" />
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Access change · {targetUserLabel}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {summary.groupsAdded + summary.permsAdded} addition
                {summary.groupsAdded + summary.permsAdded === 1 ? '' : 's'},{' '}
                {summary.groupsRemoved + summary.permsRemoved} removal
                {summary.groupsRemoved + summary.permsRemoved === 1 ? '' : 's'}
              </div>
            </div>
          </div>
          <Badge
            className={cn(
              'flex items-center gap-1.5 border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide',
              riskVariant.cls,
            )}
            data-risk-level={risk}
            title={`Risk level: ${risk}`}
          >
            <ShieldAlert className="h-3 w-3" />
            {riskVariant.label}
          </Badge>
        </header>

        {/* Diff columns */}
        <section
          className="grid gap-3 md:grid-cols-2"
          data-permission-diff="true"
        >
          {/* Current */}
          <div className="rounded-md border border-border/60 bg-card p-3">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Current permissions
            </h3>

            <div className="space-y-3">
              <div>
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Groups
                </div>
                <DiffList
                  title="Groups (current)"
                  side="left"
                  unchanged={groupsDiff.unchanged}
                  removed={groupsDiff.removed}
                />
              </div>
              <div>
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Permissions
                </div>
                <DiffList
                  title="Permissions (current)"
                  side="left"
                  unchanged={permsDiff.unchanged}
                  removed={permsDiff.removed}
                />
              </div>
            </div>
          </div>

          {/* Proposed */}
          <div className="rounded-md border border-border/60 bg-card p-3">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Proposed permissions
            </h3>

            <div className="space-y-3">
              <div>
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Groups
                </div>
                <DiffList
                  title="Groups (proposed)"
                  side="right"
                  unchanged={groupsDiff.unchanged}
                  // 'removed' on the right side is rendered as additions —
                  // we pass `added` through the same prop because the
                  // visual treatment is symmetric (Plus + green vs Minus +
                  // red, both line above the unchanged row).
                  removed={groupsDiff.added}
                />
              </div>
              <div>
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Permissions
                </div>
                <DiffList
                  title="Permissions (proposed)"
                  side="right"
                  unchanged={permsDiff.unchanged}
                  removed={permsDiff.added}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Justification — keep it under the diff so the reviewer can write
            it after reading the change. */}
        {justificationField ? (
          <section data-justification-field="true">
            <FieldRow
              approvalId={approval.id}
              field={{
                ...justificationField,
                // Force multiline rendering even if the author declared a
                // single-line `text` type. Justifications are paragraphs.
                type:
                  justificationField.type === 'text'
                    ? 'multiline'
                    : justificationField.type,
                label: justificationField.label || 'Justification',
              }}
              value={readPath(evidence, justificationField.path)}
              onChange={(v) => onFieldChange(justificationField.path, v)}
              disabled={disabled}
            />
          </section>
        ) : justificationValue ? (
          <section className="rounded-md border border-border/60 bg-muted/30 p-3 text-xs">
            <div className="mb-1 font-semibold uppercase tracking-wide text-muted-foreground">
              Justification
            </div>
            <p className="whitespace-pre-wrap text-muted-foreground">
              {justificationValue}
            </p>
          </section>
        ) : null}

        {/* Extras */}
        {extras.length > 0 && (
          <section className="space-y-3 border-t border-border/60 pt-3">
            {extras.map((f) => (
              <FieldRow
                key={f.path}
                approvalId={approval.id}
                field={f}
                value={readPath(evidence, f.path)}
                onChange={(v) => onFieldChange(f.path, v)}
                disabled={disabled}
              />
            ))}
          </section>
        )}

        <ActionBar
          actions={schema.actions}
          approvalId={approval.id}
          onDecide={onDecide}
          disabled={disabled}
          className="rounded-md"
        />
      </div>

      {/* Right rail */}
      <aside className="space-y-3">
        {/* Summary card so reviewers can sanity-check at a glance. */}
        <section className="rounded-md border border-border/60 bg-card p-3 text-xs">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Change summary
          </h4>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
            <dt className="text-muted-foreground">Groups added</dt>
            <dd className="text-right font-medium tabular-nums">
              {summary.groupsAdded}
            </dd>
            <dt className="text-muted-foreground">Groups removed</dt>
            <dd className="text-right font-medium tabular-nums">
              {summary.groupsRemoved}
            </dd>
            <dt className="text-muted-foreground">Permissions added</dt>
            <dd className="text-right font-medium tabular-nums">
              {summary.permsAdded}
            </dd>
            <dt className="text-muted-foreground">Permissions removed</dt>
            <dd className="text-right font-medium tabular-nums">
              {summary.permsRemoved}
            </dd>
          </dl>
        </section>

        {schema.contextPanels.map((panel) => (
          <section
            key={panel.panelId}
            className="rounded-md border border-border/60 bg-card p-3"
          >
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {panel.title}
            </h4>
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px]">
              {JSON.stringify(
                panel.dataPath
                  ? readPath(evidence, panel.dataPath)
                  : panel.fields?.length
                    ? Object.fromEntries(
                        panel.fields.map((f) => [
                          f.label,
                          readPath(evidence, f.path) ?? null,
                        ]),
                      )
                    : evidence,
                null,
                2,
              )}
            </pre>
          </section>
        ))}
      </aside>
    </div>
  );
};
